import React from 'react';
import {useShallow} from 'zustand/react/shallow';

import {Analytics} from '../../../../services/analytics';
import {toMongo} from '../../../../util/filters';
import {useAutomationsOrgScope} from '../../../Automations/hooks/useAutomationsOrgScope';
import {getDefaultTriggerDescription} from '../utils';
import {useActionConfigStore} from './ActionStore';
import {AutomationStoreActions, useAutomationStore} from './AutomationStore';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {useEventStore} from './EventStore';
import {useRunMetricChangeStore} from './RunMetricChangeSection';
import {useRunMetricThresholdStore} from './RunMetricThresholdSection';
import {useRunMetricZscoreStore} from './RunMetricZscoreSection';
import {useRunStateStore} from './RunStateSection';
import {useSandboxStateStore} from './SandboxStateSection';
import {AutomationSummarySection} from './shared';
import {EntryPoint} from './types';
import {useWeaveMetricThresholdStore} from './WeaveMetricAlertSection';

/**
 * Page 3 of Create trigger drawer
 * Add name and description for the trigger
 */

type DrawerPageSummaryProps = {
  triggerNameExists: boolean;
};

export const DrawerPageSummary = ({
  triggerNameExists,
}: DrawerPageSummaryProps) => {
  // These update on every state change, BUT! the data in them is not being
  // modified in the summary section, so it won't cause a ton of re-renders.
  /* oxlint-disable wandb/zustand-use-store-selectors */
  const eventConfig = useEventStore();
  const actionConfig = useActionConfigStore();
  const runMetricConfig = useRunMetricThresholdStore();
  const runMetricChangeConfig = useRunMetricChangeStore();
  const runStateConfig = useRunStateStore();
  const weaveMetricThresholdConfig = useWeaveMetricThresholdStore();
  const sandboxStateConfig = useSandboxStateStore();
  /* oxlint-enable wandb/zustand-use-store-selectors */
  const notificationConfig = actionConfig.notification;
  const triggerName = useAutomationStore(useShallow(s => s.triggerName));
  const triggerDescription = useAutomationStore(
    useShallow(s => s.triggerDescription)
  );
  const runMetricZscoreConfig = useRunMetricZscoreStore.getState();

  // For an organization hub scope, EventStore.entityName is the org *entity*
  // name; the generated description should read as the org name instead.
  const {orgName} = useAutomationsOrgScope();
  const descriptionEntityName =
    eventConfig.automationsHubScopeKind === EntryPoint.ORGANIZATION && orgName
      ? orgName
      : eventConfig.entityName;

  const descriptionDict = getDefaultTriggerDescription(
    {
      eventType: eventConfig.eventType,
      triggerScopeType: eventConfig.scopeType,
      entityName: descriptionEntityName,
      projectName: eventConfig.projectName,
      artifactCollectionName: eventConfig.scopeArtifactCollectionName,
      filters: JSON.stringify(toMongo(eventConfig.filters)),
      runMetricConfig,
      runMetricChangeConfig,
      notificationConfig,
      runStateConfig,
      runMetricZscoreConfig,
      weaveMetricThresholdConfig,
      sandboxStateConfig,
      ariaPrompt: actionConfig.aria.prompt,
    },
    actionConfig.actionType,
    actionConfig.job.queuedJob?.name ?? '',
    actionConfig.job.queuedJobAlias ?? '',
    actionConfig.webhook.webhook?.name,
    actionConfig.notification.teamSlackIntegration?.channelName
  );

  // Add useEffect to track when page is viewed
  React.useEffect(() => {
    Analytics.track('Automation Creation', {
      type: 'page_view',
      page: 'summary',
      eventType: eventConfig.eventType,
      actionType: actionConfig.actionType,
      entityName: eventConfig.entityName,
    });
    // Only track on initial render
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <S.PageLabelRow>Step 3 of 3 - Summary</S.PageLabelRow>
      <AutomationSummarySection
        eventTitle={descriptionDict.event.title}
        eventBody={descriptionDict.event.body}
        actionTitle={descriptionDict.action.title}
        actionBody={descriptionDict.action.body}
      />
      <S.LabelRow>
        <S.Label>Automation name</S.Label>
      </S.LabelRow>
      <S.StyledTextField
        placeholder="Name your automation"
        value={triggerName ?? ''}
        onChange={value => AutomationStoreActions.setTriggerName(value)}
        autoFocus={true}
      />
      {triggerNameExists && (
        <S.ErrorText>Automation name already exists.</S.ErrorText>
      )}
      <S.LabelRow>
        <S.Label>Description</S.Label>
      </S.LabelRow>
      <S.StyledTextArea
        value={triggerDescription ?? ''}
        placeholder={`Enter description (optional) \n\n${descriptionDict.stringVal}`}
        onChange={event =>
          AutomationStoreActions.setTriggerDescription(event.target.value)
        }
      />
    </>
  );
};
